/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable eqeqeq */
import { AgGridEvent, CellClassParams, ColDef, ColumnApi, ColumnPinnedType, ColumnState, ExpressionService, GetContextMenuItemsParams, GetLocaleTextParams, GetMainMenuItems, GetMainMenuItemsParams, GetQuickFilterTextParams, GridApi, GridOptions, ICellRendererComp, ICellRendererFunc, ICellRendererParams, IFilterParams, IRowNode, ITooltipParams, MenuItemDef, RowDataTransaction, ValueFormatterParams, ValueGetterParams } from "ag-grid-community";
import AgGridChartBase from "src/AngularJS/ag-grid-chart.abstract";
import { GridHelperOptions, UIGridColumnState, UIGridTableState } from "src/shared/models/ag-grid-helper.models";
import type { ModuleManager } from "src/shared/models/module-manager";
import type MetricData from "src/AngularJS/Models/metric-data";
import CVLoadingOverlay from "src/shared/ag-grid-components/cv-loading-overlay";
import FirstColCellRenderer from "src/shared/ag-grid-components/first-col-cell-renderer";
import GroupDrillDownCellRenderer from "src/shared/ag-grid-components/group-cell-renderer";
import GroupHeaderComp from "src/shared/ag-grid-components/group-header";
import MetricAnimateShowChangeCellRenderer from "src/shared/ag-grid-components/metric-animate-show-change-cell-renderer";
import ModuleDataGroupItem from "src/shared/models/module-data-group-item";
import { CvLocalizationServiceBase } from "src/shared/services/cv-localization-service-base";
import SimpleContextMenuRenderer from '../ag-grid-components/simple-context-menu';
import { Modules } from "../models/module-info.model";
import { ClearViewUtils } from "../utils";
import { AgContextMenuServiceBase, GroupContextMenuParams, MetricContextMenuParams } from "./ag-context-menu-service-base";
import { ModuleHelperServiceBase } from "./module-helper-service-base";
import deepcopy from "deepcopy";
import { CvConfigurationServiceBase } from "./cv-configuration-service-base";
import { ComponentFactoryResolver, Inject } from "@angular/core";
import { CvApiServiceBase } from "./cv-api-service-base";

export class AgGridHelperServiceBase extends AgGridChartBase {
    // make components available to javascript
    public GroupHeaderComp: typeof GroupHeaderComp = GroupHeaderComp;
    public FirstColCellRenderer: typeof FirstColCellRenderer = FirstColCellRenderer;
    public GroupDrillDownCellRenderer: typeof GroupDrillDownCellRenderer = GroupDrillDownCellRenderer;
    public MetricAnimateShowChangeCellRenderer: typeof MetricAnimateShowChangeCellRenderer = MetricAnimateShowChangeCellRenderer;
    public loadingOverlay: typeof CVLoadingOverlay = CVLoadingOverlay;
    public contextMenuLocalization: Map<string, string>;
    public mainMenuLocalization: Map<string, string>;

    // Used by getLocalizations() and localeTextFunc().
    // Not stored in the data dictionary above to discourage direct usage
    private _localizations: { [key: string]: string } = null;

    private expressionService: ExpressionService;

    public defaultMinWidth = 75;

    // #region Functions and Classes meant for GridOptions

    public locationColDef = { //renamed to Parent
        headerName: "Parent",
        field: "parentGroupData",
        filter: 'agTextColumnFilter',
        hide: true,
        type: "groupColumn",
        pinnedRowCellRenderer: () => "",
    };

    private readonly GIVEAWARD: string = "GiveAward";

    public metricComparator = function (
        valueA: MetricData | string,
        valueB: MetricData | string,
        nodeA: IRowNode,
        nodeB: IRowNode,
        isInverted: boolean
    ) {
        if (nodeA === undefined || nodeB === undefined) {
            const stringA = valueA as string;
            const stringB = valueB as string;
            // when opening column menus, ag-Grid calls this function without passing in the nodes, just return a regular string compare
            return stringA.localeCompare(stringB);
        }

        const field = this?.field;
        const dataA: MetricData = (valueA as MetricData) || (field && nodeA.data?.[field]);
        const dataB: MetricData = (valueB as MetricData) || (field && nodeB.data?.[field]);
        const metricValueA = dataA?.MetricValue;
        const metricValueB = dataB?.MetricValue;


        if (metricValueA == null && metricValueB == null)
            return 0;
        else if (metricValueA == null) {
            return isInverted ? -1 : 1;
        } else if (metricValueB == null) {
            return isInverted ? 1 : -1;
        } else {
            return metricValueA - metricValueB;
        }
    };

    public metricValueEquals = (a: MetricData, b: MetricData) => {
        if (a == null || b == null)
            return false;

        if (a.MetricValue !== b.MetricValue && a.MetricValue !== null)
            return false;

        return JSON.stringify(a.objectiveData) === JSON.stringify(b.objectiveData);
    };

    public metricValueFormatter = (params: ValueFormatterParams) => {
        if (params.value?.MetricDisplayValue !== undefined)
            return params.value.MetricDisplayValue || "";
        else if (params.value) {
            const metricInfo = params.context.getMetric(params);
            if (metricInfo)
                return this.moduleHelperService.formatMetricValue(params.value, metricInfo.NumericType, metricInfo.TimeType);
            else
                return params.value;
        }
        else
            return null;
    };

    public metricObjectiveStyle = (params: CellClassParams) => {
        const styling = { 'background-color': 'inherit' };
        const currentMetric: MetricData = params.value || params.data?.[params.colDef.field];
        if (currentMetric?.HasPO && currentMetric?.objectiveData?.poValueRgbaColor &&
            currentMetric.MetricValue != null && !currentMetric.hasThreshold
        ) {
            styling["background-color"] = currentMetric.objectiveData.poValueRgbaColor;
        }

        return styling;
    };

    public groupValueFormatter = (params: ValueFormatterParams) => {
        const groupData: ModuleDataGroupItem = params.value || params.data?.[params.colDef.field];
        return groupData?.groupName;
    };

    public groupTooltipValueGetter = (e: ITooltipParams) => {
        const groupData: ModuleDataGroupItem = e.value || e.data?.[(e.colDef as ColDef).field];
        if (!groupData?.groupHierarchy && !groupData?.groupName) { // null, undefined, or empty strings will just return null.
            return null;
        }
        else if (!groupData?.groupHierarchy && groupData?.groupName) { // when viewing by company (drilling up to hightest level) so hierarchy doesnt exist
            return groupData?.groupName;
        }
        else {
            return (groupData?.groupHierarchy + " > " + groupData?.groupName);
        }
    };

    public groupComparator = (valueA: ModuleDataGroupItem, valueB: ModuleDataGroupItem) => {
        const groupNameA = valueA?.groupName;
        const groupNameB = valueB?.groupName;

        if (groupNameA == null && groupNameB == null)
            return 0;
        else if (groupNameA == null && groupNameB != null)
            return -1;
        else if (groupNameA != null && groupNameB == null)
            return 1;
        else
            return groupNameA.localeCompare(groupNameB, undefined, { sensitivity: "base" });
    };

    public groupQuickFilterText = (params: GetQuickFilterTextParams) => {
        const groupData: ModuleDataGroupItem = params.value || params.data?.[params.colDef.field];
        if (typeof groupData === 'string')
            return groupData;
        return groupData?.groupName;
    };

    public groupFilterValueGetter = (params: ValueGetterParams) => {
        const groupData: ModuleDataGroupItem = params.getValue(params.colDef.field) || params.data?.[params.colDef.field];
        if (typeof groupData === 'string')
            return groupData;
        return groupData?.groupName;
    };

    public dateTypeComparator = (filterLocalDateAtMidnight, cellValue) => {
        const dateAsString = cellValue;
        const dateParts = dateAsString.split("/");
        const cellDate = new Date(Number(dateParts[2]), Number(dateParts[0]) - 1, Number(dateParts[1]));

        if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) {
            return 0;
        }

        if (cellDate < filterLocalDateAtMidnight) {
            return -1;
        }

        if (cellDate > filterLocalDateAtMidnight) {
            return 1;
        }
    };

    getContextMenuItems = (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
        const menuItemsByKey: { [key: string]: MenuItemDef } = {};
        const moduleManager: ModuleManager = params.context?.moduleManager;
        const moduleKey = moduleManager?.getModuleKey();
        const moduleId = moduleManager?.getModuleInfo?.()?.ModuleID;

        if (!params.column || (params.node.group && !this.isIntervalListByGroup(moduleManager)) ||
            moduleManager?.isModuleSubscriptionPreview
        ) {
            // right clicked on empty data or a row-group
            return [];
        }

        const colDef = params.column.getColDef();
        const groupColumnFields = new Set(['Group', 'Agent', 'Name', 'Team']);

        const writeAccess = moduleManager?.hasWriteAccess() ?? false;
        const readGroupAccess = moduleManager?.hasReadGroupAccess() ?? false;
        const agentStateDetailsAccess = moduleManager?.hasAgentStateDetailsAccess() ?? false;
        const giveAwardManagerAccess = moduleManager?.hasGiveAwardManagerAccess() ?? false;
        const suggestCoachingAccess = moduleManager?.hasSuggestCoachingAccess() ?? false;



        if ((colDef.type === 'groupColumn' || groupColumnFields.has(colDef.field)) &&
            (writeAccess || readGroupAccess || this.configurationService.isCxOne)
        ) { //add Agent State Details regardless of access for CXone
            // group specific commands
            const groupData: ModuleDataGroupItem =
                !this.isIntervalListByGroup(moduleManager) ?
                    (params.node?.data.groupData || params.node?.data.group || params.node?.data[colDef.field]) :
                    params.context.getGroupData(params);

            if (groupData && groupData?.groupLevel > -1) {
                const contextMenuParams: GroupContextMenuParams = { moduleKey, groupData, moduleTypeId: moduleId };

                menuItemsByKey['IntervalReport'] = {
                    icon: this.configurationService.isCxOne ? `<svg class="icon-18 stroke-iconMenu">
                            <use href="../App_Themes/CXone/icons/chart-type.svg#chart-type"></use>
                            </svg>` : null,
                    name: this.configurationService.isCxOne ?
                        this.getContextMenuLocalizedName("Open_Interval_Report_CXone", 'Open Interval Report') :
                        this.getContextMenuLocalizedName("Open_Interval_Report", "Open Interval Report"),
                    action: () => this.contextMenuService.intervalReportClicked.next(contextMenuParams),
                };

                if (!moduleManager.container.dashboardManager.isCXOne) {
                    menuItemsByKey['ProfilePreview'] = {
                        name: this.getContextMenuLocalizedName("Open_Profile_Preview", "Open Profile Preview"),
                        action: () => this.contextMenuService.profilePreviewClicked.next(contextMenuParams),

                    };
                }

                // eslint-disable-next-line eqeqeq
                if (groupData.groupLevel === 1 && groupData.groupType == "E") {
                    // Level 1 (Agent) only items
                    if (this.configurationService.isCxOne) {
                        if (agentStateDetailsAccess) {
                            menuItemsByKey['AgentStateDetails'] = {
                                icon:`<svg class="icon-18 fill-iconDefault-noHover"><use href="../App_Themes/CXone/icons/legend.svg#legend"></use></svg>`,
                                name: this.getContextMenuLocalizedName("Agent_State_Details", "Agent State Details"),
                                action: () => this.contextMenuService.agentDetailsClicked.next(contextMenuParams),
                            };
                        }

                        if (giveAwardManagerAccess) {
                            menuItemsByKey[this.GIVEAWARD] = {
                                icon:`<svg class="icon-18 fill-award-icon"><use href="../App_Themes/CXone/icons/give-award.svg#give-award"></use></svg>`,
                                name: this.getContextMenuLocalizedName("Give_Award", "Give Award"),
                                cssClasses: ['give-award-space'],
                                action: () => this.contextMenuService.giveAwardClicked.next(contextMenuParams),
                            };
                        }
                    } else {
                        menuItemsByKey['AgentStateDetails'] = {
                            name: this.getContextMenuLocalizedName("Agent_State_Details", "Agent State Details"),
                            action: () => this.contextMenuService.agentDetailsClicked.next(contextMenuParams),
                        };
                        menuItemsByKey[this.GIVEAWARD] = {
                            name: this.getContextMenuLocalizedName("Give_Award", "Give Award"),
                            action: () => this.contextMenuService.giveAwardClicked.next(contextMenuParams),
                        };
                    }

                    if (suggestCoachingAccess) {
                        menuItemsByKey['SuggestCoaching'] = {
                            name: this.getContextMenuLocalizedName("Suggest_Coaching_Session", "Suggest Coaching Session"),
                            action: () => this.contextMenuService.suggestCoachingClicked.next(contextMenuParams),
                        };
                        this.contextMenuService.hasPermission.SuggestCoaching = true;
                    }

                    menuItemsByKey['CreateTask'] = {
                        name: this.getContextMenuLocalizedName("Create_Task", "Create a Task"),
                        action: () => this.contextMenuService.createTaskClicked.next(contextMenuParams),
                    };
                }

                if (!moduleManager.container.dashboardManager.isCXOne) {
                    menuItemsByKey['ExportSingleRow'] = {
                        name: this.getContextMenuLocalizedName("Export_Row_Information", "Export Row Information"),
                        subMenu: [
                            {
                                name: this.getContextMenuLocalizedName("Excel_with_Objectives", "Excel with Objectives"),
                                icon: `<svg class="icon-16 fill-casper" style="fill: #6a7480;">
                                            <use href="../App_Themes/clearview-default/icons/utility/excel-objectives.svg#excel-objectives">
                                        </use></svg>`,
                                action: () => this.contextMenuService.singleRowExportClicked
                                    .next({...contextMenuParams, format: 'excel', moduleName: moduleManager?.getModuleInfo()?.ModuleName}),
                            },
                            {
                                name: this.getContextMenuLocalizedName("Excel", "Excel"),
                                icon: `<svg class="icon-16 fill-casper" style="fill: #6a7480;">
                                            <use href="../App_Themes/clearview-default/icons/utility/excel.svg#excel">
                                        </use></svg>`,
                                action: () => this.contextMenuService.singleRowExportClicked.next({...contextMenuParams,
                                    format: 'excelNoObjectives', moduleName: moduleManager?.getModuleInfo()?.ModuleName}),
                            },
                            {
                                name: this.getContextMenuLocalizedName("CSV", "CSV"),
                                icon: `<svg class="icon-16 fill-casper" style="fill: #6a7480;">
                                            <use href="../App_Themes/clearview-default/icons/utility/csv.svg#csv">
                                        </use></svg>`,
                                action: () => this.contextMenuService.singleRowExportClicked.next({...contextMenuParams,
                                    format: 'csv', moduleName: moduleManager?.getModuleInfo()?.ModuleName}),
                            },
                        ],
                    };
                }
            }
        } else if (colDef.type === 'metricColumn') {
            // metric specific commands
            const metricData: MetricData = params.value || params.node.data[colDef.field];
            const groupData: ModuleDataGroupItem = params.node?.data.groupData || params.node?.data.group;

            if (metricData && groupData) {
                const contextMenuParams: MetricContextMenuParams = { moduleKey, groupData, metricData };

                const detailViewMenuItem = this.contextMenuService.createDetailLevelMenuItem(contextMenuParams);
                if (detailViewMenuItem != null)
                    menuItemsByKey['DetailLevelView'] = detailViewMenuItem;
            }
        }

        // filter by module
        const menuItems: (string | MenuItemDef)[] = [];

        // if left empty, it will allow all modules that fit the criteria above
        const ALL_MODULES = new Set([]);

        const giveAwardModules = this.configurationService.isCxOne ? 
        new Set([
            Modules.MetricsSummary,
            Modules.MetricsInterval,
            Modules.CurrentAgentStates,
            Modules.Leaderboard,
        ]) :
        ALL_MODULES;

        const modulesByMenuItem: { [key: string]: Set<Modules> } = {
            IntervalReport: new Set([
                Modules.Summary,
                Modules.MetricsSummary,
                Modules.Leaderboard,
            ]),
            ProfilePreview: ALL_MODULES,
            AgentStateDetails: ALL_MODULES,
            SuggestCoaching: ALL_MODULES,
            GiveAward: giveAwardModules,
            CreateTask: ALL_MODULES,
            DetailLevelView: ALL_MODULES,
            ExportSingleRow: new Set([
                Modules.Summary,
            ]),
        };

        for (const key of Object.keys(menuItemsByKey ?? {})) {
            const menuItem = menuItemsByKey[key];
            const isCorrectModule = (modulesByMenuItem[key].size === 0 || modulesByMenuItem[key].has(moduleId));
            const hasPermission = this.contextMenuService.hasPermission[key];

            if (menuItem.cssClasses?.length > 0) {
                menuItem.cssClasses.push('icon-margin')
            } else {
                menuItem.cssClasses = ['icon-margin'];
            }

            if (isCorrectModule && hasPermission) {
                if (!moduleManager.container.dashboardManager.isCXOne) {
                    menuItems.push(menuItem);
                }
                else {
                    // eslint-disable-next-line eqeqeq
                    if (menuItem.name != 'ProfilePreview')
                        menuItems.push(menuItem);
                }
            }
        }

        if (this.contextMenuItems.has(moduleKey))
            menuItems.push(...this.contextMenuItems.get(moduleKey));

        return menuItems;
    };

    getMainMenuItems: GetMainMenuItems = (params: GetMainMenuItemsParams) => {
        const items: (string | MenuItemDef)[] = params.defaultItems;
        const index = params.defaultItems.findIndex(item => item == "resetColumns");
        items[index] = {
            name: this.getMainMenuItemLocalization("resetColumns", "Reset Columns"),
            action: () => {
                params.api.getColumns().forEach(col => {
                    col['gridOptionsService'].api.resetColumnState();
                });
                this.sizeColumnsFromApisSmart(params.api);
            }
        };
        return items;
    };

    public basicCellRenderer: ICellRendererFunc = (params: ICellRendererParams) => params.value;

    // #endregion

    // #region GridOptions

    // tooltip added to gridoptions in ag-grid-helper-service.ts uses AgGridGroupHeaderTooltipComponent component
    // eslint-disable-next-line @typescript-eslint/member-ordering
    baseGridOptions: GridOptions = {
        defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
        },
        accentedSort: true,
        animateRows: true,
        rowSelection: "multiple",
        suppressCellFocus: true,
        suppressScrollOnNewData: true,
        suppressClipboardPaste: true,
        suppressCopyRowsToClipboard: true,
        skipHeaderOnAutoSize: this.configurationService?.isCxOne ?? false,

        columnDefs: [],
        rowData: [],

        getContextMenuItems: this.getContextMenuItems,
        getMainMenuItems: this.getMainMenuItems,

        columnTypes: {
            groupColumn: {
                valueFormatter: this.groupValueFormatter,
                tooltipValueGetter: this.groupTooltipValueGetter,
                comparator: this.groupComparator,
                getQuickFilterText: this.groupQuickFilterText,
                filterValueGetter: this.groupFilterValueGetter,
                filter: "agTextColumnFilter",
            },
            metricColumn: {
                cellClass: 'ag-numeric-cell ag-right-aligned-cell',
                cellRenderer: MetricAnimateShowChangeCellRenderer,
                cellStyle: this.metricObjectiveStyle,
                comparator: this.metricComparator,
                equals: this.metricValueEquals,
                filter: 'agNumberColumnFilter',
                filterParams: <IFilterParams> {
                    getValue(node: IRowNode) {
                        return (node.data[this.colDef.field] as MetricData)?.MetricValue;
                    },
                },
                getQuickFilterText: (params) => (params.value as MetricData)?.MetricDisplayValue,
                headerClass: 'ag-numeric-header ag-right-aligned-header',
                valueFormatter: this.metricValueFormatter,
            },
            ellipsesColumn: {
                cellRenderer: (params) => `<div class="text-truncate">${window['htmlEncode'](params.value)}</div>`,
                tooltipValueGetter: (params) => params.value
            },
            simpleContextMenu: {
                cellRenderer: SimpleContextMenuRenderer,
            },
            numberColumn: {
                cellClass: 'ag-numeric-cell ag-right-aligned-cell',
                headerClass: 'ag-numeric-header ag-right-aligned-header',
            }
        },

        processCellForClipboard: (params) => "",
    };

    // #endregion GridOptions

    private contextMenuItems: Map<string, (string | MenuItemDef)[]> = new Map();

    constructor(
        fromSharedService: boolean,
        private contextMenuService?: AgContextMenuServiceBase,
        private moduleHelperService?: ModuleHelperServiceBase,
        private localizationService?: CvLocalizationServiceBase,
        private configurationService?: CvConfigurationServiceBase,
    ) {
        super();
        if (fromSharedService)
            return AgGridHelperServiceBase.getServiceBase();

        this.contextMenuService = contextMenuService ?? AgContextMenuServiceBase.getServiceBase();
        this.moduleHelperService = moduleHelperService ?? ModuleHelperServiceBase.getServiceBase();
        this.localizationService = localizationService ?? CvLocalizationServiceBase.getServiceBase();
        this.configurationService = configurationService ?? CvConfigurationServiceBase.getServiceBase();

        if(this.configurationService.isCxOne) {
            this.baseGridOptions.icons = {
                sortAscending: `<div class="sortIcons">
                                    <svg class="icon-10 fill-cxoneBlue"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg>
                                    <svg class="icon-10 fill-sort"><use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use></svg>
                                </div>`,
                sortDescending: `<div class="sortIcons">
                                    <svg class="icon-10 fill-sort"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg>
                                    <svg class="icon-10 fill-cxoneBlue"><use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use></svg>
                                </div>`,
                clipboardCopy: '<svg class="icon-18 fill-iconDefault-noHover ml-n2"><use href="../App_Themes/CXone/icons/duplicate.svg#duplicate"></use></svg>',
                menu: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/widget-menu.svg#widget-menu"></use></svg>',
                columns: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/chart.svg#chart"></use></svg>',
                filter: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/filter.svg#filter"></use></svg>',
            };
            this.baseGridOptions.localeText = {
                noRowsToShow: null
            };
            this.baseGridOptions.headerHeight = 28;
            this.baseGridOptions.rowHeight = 28;
        }else {
            this.baseGridOptions.icons = null;
            this.baseGridOptions.localeText = {
                noRowsToShow: `<svg class="fill-inversion mb-3" style="width:48px; height:48px"><use href="../App_Themes/clearview-default/icons/utility/search.svg#search"></use></svg>
                               <div class="font-size-12 lato-bold color-theUpsideDown mb-2">${this.getContextMenuLocalizedName("No_Data", "No Data")}</div>
                               <div class="font-size-12 color-whaleTail" style="max-width:178px; text-align:center">${this.getContextMenuLocalizedName("No_Data_Message", "Try adjusting your settings, or wait for data to be created.")}</div>`
            };
        }

        this.loadLocalizationData();



        this.expressionService = new ExpressionService();
    }

    static getServiceBase(): AgGridHelperServiceBase {
        return ClearViewUtils.getSharedService(AgGridHelperServiceBase, 'AgGridHelperServiceBase');
    }

    createGridOptions(options: GridOptions, helperOptions?: GridHelperOptions) {
        if (window.location.pathname.includes('SharedDashboard.aspx')) {
            options.defaultColDef = options.defaultColDef ?? {};
            options.defaultColDef.suppressMenu = true;
        }

        if (helperOptions)
            this.applyHelperOptions(options, helperOptions);

        return Object.assign(deepcopy(this.baseGridOptions), options);
    }

    createTransaction<T>(oldData: T[], newData: T[], gridOptions: GridOptions) {
        const transaction: RowDataTransaction = {
            add: [],
            remove: [],
            update: [],
        };

        if (gridOptions.getRowId) {
            const oldIds = oldData?.reduce((oldMap, oldItem: any) => {
                const id = gridOptions.getRowId(oldItem);
                oldMap[id] = oldItem;
                return oldMap;
            }, new Map<string, T>()) ?? new Map<string, T>();

            const params = { data: null, level: 0, api: gridOptions.api, columnApi: gridOptions.columnApi, context: gridOptions.context };
            for (const newItem of newData) {
                params.data = newItem;
                const id = gridOptions.getRowId(params);
                if (oldIds.has(id)) {
                    transaction.update.push(newItem);
                    oldIds.delete(id);
                } else
                    transaction.add.push(newItem);
            }

            // any values left in oldIds are to be removed
            for (const oldItem of oldIds.values()) {
                transaction.remove.push(oldItem);
            }

        } else {
            transaction.remove = oldData;
            transaction.add = newData;
        }

        return transaction;
    }


    //#region Localization

    // Either the object localeText can be provided or the function localeTextFunc to gridOptions to localize an ag-grid.
    /**
     * Returns a promise that resolves into a plain object with keys being the strings used by ag-grid and the values being the replacement, localized strings.
     * This object is used as the value to gridOptions.localeText
     */
    getLocalizations() {
        const promise = new Promise<{ [key: string]: string }>(async (resolve, reject) => {
            // If _localizations has been set, save effort and don't recalculate it.
            if (this._localizations != null) {
                resolve(this._localizations);
                return;
            }

            try {
                const response = await this.localizationService.GetSection("AgGrid");
                if (this._localizations != null) {
                    // If this is set now when it wasn't prior, there must have been another promise dispatched in between.
                    resolve(this._localizations);
                    return;
                }

                if (typeof response === "object") {
                    // cvLocalizationFactory returns an object where the values are arrays of strings.
                    // Since ag-grid expects the value to be a simple string, the response has to be
                    // "unpackaged" from an array of strings to *just* the value of index 0, if it's a string.
                    const localizations = {};
                    response.forEach((value, key) => {
                        localizations[key] = value;
                    });
                    this._localizations = localizations;
                    resolve(this._localizations);
                } else {
                    reject(new Error("Response for AgGrid localizations wasn't an object"));
                }
            } catch (err) {
                reject(err);
            }
        });

        return promise;
    }

    /**
     * @deprecated Use `getLocaleText` instead.
     * This function conforms to https://www.ag-grid.com/javascript-grid-internationalisation/#using-localetextfunc
     * This function is used as the value to gridOptions.localeTextFunc.
     * Ag-grid calls localeTextFunc with the `key` for the string wanted and its `defaultValue` that should be
     * returned if there isn't a different value for it.
     * If this function is called before the localizations have been loaded, `defaultValue` will be returned.
     */
    public localeTextFunc = (key: string, defaultValue: string) => {
        if (this._localizations?.[key]) {
            return this._localizations?.[key];
        } else return defaultValue;
    };

    /**
     * This function conforms to https://www.ag-grid.com/javascript-grid-internationalisation/#using-localetextfunc
     * This function is used as the value to gridOptions.localeTextFunc.
     * Ag-grid calls localeTextFunc with the `key` for the string wanted and its `defaultValue` that should be
     * returned if there isn't a different value for it.
     * If this function is called before the localizations have been loaded, `defaultValue` will be returned.
     */
    public getLocaleText = (params: GetLocaleTextParams<any, any>): string => {
        if (this._localizations?.[params.key]) {
            return this._localizations?.[params.key];
        } else return params.defaultValue;
    };

    //#endregion Localization

    public createClickableGroupRenderer(clickCallback: (params: ICellRendererParams) => void) {
        if (typeof clickCallback !== 'function')
            clickCallback = () => {};

        const ClickableGroupRenderer = class implements ICellRendererComp {
            private gui: HTMLElement;
            init(params: ICellRendererParams) {
                this.gui = document.createElement('div');
                this.gui.classList.add('agentName', 'clickable', 'd-flex', 'h-100', 'align-items-center');

                const text = document.createElement('span');
                text.innerText = params.value;
                this.gui.appendChild(text);

                this.gui.addEventListener('click', () => clickCallback(params));
            }

            refresh() {
                return false;
            }

            getGui() {
                return this.gui;
            }
        };

        return ClickableGroupRenderer;
    }



    public doesExternalFilterPass = (node: IRowNode, filterValue: string) => {
        // This function gets all the column objects, filters only to the visible columns...
        const columnApi: ColumnApi = node['beans'].columnApi;
        const gridApi: GridApi = node['beans'].gridApi;
        const filterValueLowercase = filterValue.toLowerCase();
        const visibleColumns = columnApi.getColumns().filter(col => col.isVisible());
        // ...and looks for a substring match in each of the column's values (after transforming to a lowercase string):
        return undefined !== visibleColumns.find((col) => {
            let nodeValue = gridApi.getValue(col, node);
            if (nodeValue == null)
                nodeValue = "";

            if (typeof nodeValue !== 'string') {
                const colDef = col.getColDef();
                const params: ValueFormatterParams = {
                    api: gridApi,
                    node,
                    data: node.data,
                    colDef,
                    column: col,
                    columnApi,
                    context: node['beans'].context,
                    value: nodeValue,
                };

                if (typeof colDef.valueFormatter !== 'string')
                    nodeValue = colDef.valueFormatter?.(params);
            }

            const stringNodeValue = "" + nodeValue; // ensure other non-string, non-null values are strings.
            return stringNodeValue.toLowerCase().includes(filterValueLowercase);
        });
    };

    public doesExternalFilterPassIncludeHidden = (node: IRowNode, filterValue: string) => {
        // This function gets all the column objects, filters only to the visible columns...
        const columnApi: ColumnApi = node['beans'].columnApi;
        const gridApi: GridApi = node['beans'].gridApi;
        const filterValueLowercase = filterValue.toLowerCase();
        const visibleColumns = columnApi.getColumns();
        // ...and looks for a substring match in each of the column's values (after transforming to a lowercase string):
        return undefined !== visibleColumns.find((col) => {
            let nodeValue = gridApi.getValue(col, node);
            if (nodeValue == null)
                nodeValue = "";

            if (typeof nodeValue !== 'string') {
                const colDef = col.getColDef();
                const params: ValueFormatterParams = {
                    api: gridApi,
                    node,
                    data: node.data,
                    colDef,
                    column: col,
                    columnApi,
                    context: node['beans'].context,
                    value: nodeValue,
                };

                if (typeof colDef.valueFormatter !== 'string')
                    nodeValue = colDef.valueFormatter?.(params);
            }

            const stringNodeValue = "" + nodeValue; // ensure other non-string, non-null values are strings.
            return stringNodeValue.toLowerCase().includes(filterValueLowercase);
        });
    };

    public getGridState(gridApi: GridApi): UIGridTableState {
        const columnStates = gridApi.getColumnState();
        const filterStates = gridApi.getFilterModel();
        if (columnStates.length <= 0)
            return null; // nothing to save

        const columns = columnStates.map<UIGridColumnState>(colState => ({
            name: colState.colId,
            pinned: colState.pinned ?? null,
            sort: colState.sort ? { priority: colState.sortIndex, direction: colState.sort } : {},
            visible: !colState.hide,
            width: colState.width,
        }));

        return { columns, filterStates };
    }

    public loadGridState(gridApi: GridApi, state: UIGridTableState) {
        const columnsByName: { [colId: string]: UIGridColumnState } = {};
        const columnOrder: { [colId: string]: number } = {};
        state?.columns.forEach((col, i) => {
            columnsByName[col.name] = col;
            columnOrder[col.name] = i;
        });

        let agGridOrderedColumnState: ColumnState[] = [];
        const agGridNewColumns: ColumnState[] = [];

        gridApi.getAllGridColumns().forEach(col => {
            const colState = columnsByName[col.getColId()];
            const agState: ColumnState = { colId: col.getColId() };

            if (!colState) {
                agState.hide = col.getColDef().hide;
                agGridNewColumns.push(agState);
                return;
            }

            // sort
            if (colState.sort.priority != null) {
                agState.sort = colState.sort.direction;
                agState.sortIndex = colState.sort.priority;
            }

            // pinned
            if (colState.pinned)
                agState.pinned = colState.pinned;

            // hidden
            const colVisible = colState.visible == null || Boolean(colState.visible);
            agState.hide = !colVisible;

            // Width
            if (colState.width)
                agState.width = colState.width;

            if (columnOrder[agState.colId] != null)
                agGridOrderedColumnState[columnOrder[agState.colId]] = agState;
            else
                agGridNewColumns.push(agState);
        });

        // put the columns from state in the order they were; any new columns or columns without state will be at the end
        agGridOrderedColumnState = [...agGridOrderedColumnState.filter(s => s != null), ...agGridNewColumns];
        gridApi.applyColumnState({
            state: agGridOrderedColumnState,
            applyOrder: true,
            defaultState: {
                hide: false,
                pinned: false,
                sort: null,
                sortIndex: null,
            }
        });

        // Apply the filter model
        gridApi.setFilterModel(state?.filterStates);
    }

    /** Sizes the columns for the grid in a way fills empty space, but allows large grids to still scroll. Downside is that an AgGridEvent is required to get the size */
    public sizeColumnsSmart(event: AgGridEvent) {
        event.api.autoSizeAllColumns(false); // size all columns to fit their content (this trims all padding so extra space shows on the right)

        // Determine if the vertical scrollbar is showing by comparing the inner height of the model to the client height of the container
        const isScrolling = (event.api['rowRenderer']?.maxDivHeightScaler?.modelHeight ?? 0) > event['clientHeight'];
        const scrollbarOffset = isScrolling ? 10 : 0; // account for scrollbar in the calculation. Current style is 10 pixels.
        let containerWidth = 0;
        if (event.type == "gridReady" || event.type == "firstDataRendered")
            containerWidth = event.api?.['columnModel']?.scrollWidth + event.api?.['columnModel']?.leftWidth;
        else
            containerWidth = event['clientWidth'] - scrollbarOffset; // Total usable horizontal space

        if (!containerWidth) {
            containerWidth = event?.api?.['columnController']?.scrollWidth + event?.api?.['columnController']?.leftWidth; // If this event is a column event, try to pull the width from the columnController.
            // If any columns are pinned (and not hidden), the scroll width doesn't include them. Add their width in to get the total size of the container
            /* CCT-3312 - Kara: Changed getAllColumns() to getAllGridColumns() throughout this method because getAllColumns() does not
             * include the AutoGroupCol created from grouped rows */
            event.api.getAllGridColumns()
                .filter(col => col.isPinned() && col.isVisible())
                .forEach(col => containerWidth += col.getActualWidth());
        }
        if (!containerWidth) {
            // Something went wrong, just let the autoSizeAllColumns above handle it and return.
            return;
        }

        // Quick run down of the code below:
        // 1) Get all columns actual widget
        // 2) Find out how much space is left over on the right
        // 3) Distribute it evenly (floored) to all columns
        // 3.5) Give remainder to the final column so no space is left when done

        // Total widths of columns
        let columnWidths = 0;
        // Total columns in calculation (some columns like the 2 part group column on the breakdown mode are hidden and shouldn't be accounted for)
        let columnCount = 0;
        event.api.getAllGridColumns().forEach(col => {
            if (col.getColDef().hide || !col.isVisible()) // If column is hiding, don't calculate it
                return;

            columnWidths += col.getActualWidth(); // Add actual width of this column to the total columnWidths aggregation
            columnCount++;
        });

        // If there is space left over (not enough columns to need a horizontal scrollbar)
        if (columnWidths < containerWidth) {
            event.api.sizeColumnsToFit();
        }
    }

    /** Performs smart sizing, where space is always filled but the grid will scroll if more space is needed. Uses the grid and column APIs instead of an event. */
    public sizeColumnsFromApisSmart(gridApi: GridApi) {
        gridApi.autoSizeAllColumns(false); // size all columns to fit their content (this trims all padding so extra space shows on the right)
        const columns = gridApi.getAllDisplayedColumns();
        let autoWidth = 0;
        columns.forEach(col => autoWidth += col.getActualWidth());
        const gridWidth = gridApi['columnModel'].scrollWidth + gridApi['columnModel'].leftWidth;;
        if (autoWidth < gridWidth)
            gridApi.sizeColumnsToFit(); // take up the available space
    }

    public sizeColumns(gridApi: GridApi, gridOptions: GridOptions, minWidth: number = 50) {
        // make all columns fit their content
        gridApi.autoSizeAllColumns(false);

        // get current state so we can restore it after updating colDefs
        const state = gridApi.getColumnState();

        // make sure state doesn't have '_1' ending (weird issue with updating columns)
        state.forEach((col) => {
            const colId = col.colId;
            if (colId && colId.substr(colId.length - 2) === '_1') {
                col.colId = colId.substr(0, colId.length - 2);
            }
        });

        // because virtually hidden columns cannot calculate their width
        // set displayed columns minwidth to their content and other columns to 50
        const allColumns = gridApi.getAllGridColumns();
        const displayedColumns = gridApi.getAllDisplayedVirtualColumns();
        allColumns.forEach((column, i) => {
            (gridOptions.columnDefs[i] as ColDef).minWidth = minWidth;
        });

        // update column defs
        const colDefs = gridOptions.columnDefs;
        gridApi.setGridOption('columnDefs', []); // agGrid will not detect minWidth change unless colDefs are reset
        gridApi.setGridOption('columnDefs', colDefs);

        // restore state saved above
        gridApi.applyColumnState({ state, applyOrder: true });

        // now size the columns to fit the grid, but only if there is a height and if the width of the container is more than the width of the columns.
        const displayedActualWidth_NoGroups = displayedColumns
            .filter(c => c['colId'] !== 'Group').map(c => c.getActualWidth()).reduce((a, b) => a + b);
        // if (grid.api['gridPanel'].bodyHeight > 0 &&
        //     grid.api['gridPanel'].centerWidth > displayedActualWidth_NoGroups)
        //     grid.api.sizeColumnsToFit();

        if (((gridApi?.['columnModel']?.bodyHeight ?? 0) > 0) &&
            (gridApi?.['columnModel']?.bodyWidth ?? displayedActualWidth_NoGroups > displayedActualWidth_NoGroups))
            gridApi.sizeColumnsToFit();
    }

    public setGridReadOnly(grid: GridOptions) {
        grid.defaultColDef.resizable = false;
        grid.defaultColDef.suppressMovable = true;
        grid.defaultColDef.filter = false;
        grid.defaultColDef.suppressMenu = false;
        grid.defaultColDef.lockPinned = true;
    }

    async loadLocalization() {
        this.contextMenuLocalization = await this.localizationService.GetSection("AgGrid_ContextMenu");
        this.mainMenuLocalization = await this.localizationService.GetSection("AgGrid");
    }

    async loadLocalizationData() {
        await this.loadLocalization();

        if(this.configurationService.isCxOne) {
            this.baseGridOptions.localeText = {
                noRowsToShow: null
            };
        }else {
            this.baseGridOptions.localeText = {
                noRowsToShow: `<svg class="fill-inversion mb-3" style="width:48px; height:48px"><use href="../App_Themes/clearview-default/icons/utility/search.svg#search"></use></svg>
                               <div class="font-size-12 lato-bold color-theUpsideDown mb-2">${this.getContextMenuLocalizedName("No_Data", "No Data")}</div>
                               <div class="font-size-12 color-whaleTail" style="max-width:178px; text-align:center">${this.getContextMenuLocalizedName("No_Data_Message", "Try adjusting your settings, or wait for data to be created.")}</div>`
            };
        }

    }


    public getContextMenuLocalizedName(key: string, defaultName: string) {
        return this.contextMenuLocalization?.get(key) ?? defaultName;
    }

    public getMainMenuItemLocalization(key: string, defaultName) {
        return this.mainMenuLocalization?.get(key) ?? defaultName;
    }

    // #region Grid Helper Options Methods
    private applyHelperOptions(options: GridOptions, helperOptions: GridHelperOptions): void {
        const moduleKey: string = options.context?.moduleManager?.getModuleKey();
        if (moduleKey)
            this.contextMenuItems.set(moduleKey, []);

        if (helperOptions.enableCopy)
            this.enableCopy(options, moduleKey);

        if (helperOptions.enableChart) {
            if (moduleKey)
                this.enableChart(options, moduleKey);
            else
                console.error("GridHelperOptions.enableChart requires GridOptions to have context set with a field of moduleManager.");
        }
    }

    private enableCellSelect(options: GridOptions): void {
        options.suppressCellFocus = false;
        options.suppressRowClickSelection = true;
        options.enableRangeSelection = true;
    }

    private enableCopy(options: GridOptions, moduleKey?: string): void {
        this.enableCellSelect(options);

        if (moduleKey)
            this.contextMenuItems.get(moduleKey).push('copy');

        options.processCellForClipboard = (params) => {
            const colDef: ColDef = params.column.getColDef();
            if (typeof colDef.valueFormatter == "string" && options.components?.hasOwnProperty(colDef.valueFormatter)) {
                // Lookup Component
                return (options.components[colDef.valueFormatter] as (params) => any)(params);
            }

            else if (typeof colDef.valueFormatter == "function" && colDef.valueFormatter != null) {
                return (colDef.valueFormatter as (params) => any)(params);
            }

            return params?.value;
        };
    }

    private enableChart(options: GridOptions, moduleKey: string): void {
        this.enableCellSelect(options);
        options.enableCharts = true;
        this.contextMenuItems.get(moduleKey).push('chartRange');
    }

    // #endregion

    private isIntervalListByGroup(moduleManager: ModuleManager) {
        return moduleManager.container.moduleId === 27 && !moduleManager.customSettings['ListByInterval'].getValue();
    }
}
